import React, { useRef, useState } from "react";
import { OrgNode } from "../../../../interfaces/Organograma";

interface Props {
  nodes: OrgNode[];
  /** Ids que o usuário logado pode editar (Admin = todos; Manager = seu time; Employee = nenhum). */
  editableIds: Set<number>;
  onSetManager: (personId: number, managerId: number | null) => void;
  onEditField: (personId: number, field: "jobDescription" | "department", value: string) => void;
}

const initials = (n: string | null) =>
  (n ?? "?").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const sortByName = (a: OrgNode, b: OrgNode) =>
  (a.nmEmployee ?? "").localeCompare(b.nmEmployee ?? "", "pt");

export default function OrgChart({ nodes, editableIds, onSetManager, onEditField }: Props) {
  const dragId = useRef<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | "pool" | null>(null);
  const [zoom, setZoom] = useState(1);
  const [query, setQuery] = useState("");

  const byManager = new Map<number | null, OrgNode[]>();
  nodes.forEach((n) => {
    const key = n.managerEmployeeId ?? null;
    if (!byManager.has(key)) byManager.set(key, []);
    byManager.get(key)!.push(n);
  });
  const childrenOf = (id: number) => byManager.get(id) ?? [];
  const hasReports = (id: number) => childrenOf(id).length > 0;

  // Na árvore = tem gestor OU tem subordinados. No pool = sem gestor e sem subordinados.
  const roots = nodes.filter((n) => n.managerEmployeeId == null && hasReports(n.id)).sort(sortByName);
  const q = query.trim().toLowerCase();
  const pool = nodes
    .filter((n) => n.managerEmployeeId == null && !hasReports(n.id))
    .filter((n) => !q || (n.nmEmployee ?? "").toLowerCase().includes(q) || (n.email ?? "").toLowerCase().includes(q))
    .sort(sortByName);

  const handleDrop = (targetId: number | null) => {
    const id = dragId.current;
    dragId.current = null;
    setDropTarget(null);
    if (id == null || id === targetId) return;
    onSetManager(id, targetId);
  };

  // Remove do organograma → volta pro pool. Os subordinados sobem pro gestor dele (não ficam órfãos).
  const removeFromTree = (n: OrgNode) => {
    const grandparent = n.managerEmployeeId ?? null;
    childrenOf(n.id).forEach((k) => onSetManager(k.id, grandparent));
    onSetManager(n.id, null);
  };

  const renderNode = (n: OrgNode, depth: number, seen: Set<number>): JSX.Element | null => {
    if (seen.has(n.id)) return null;
    seen.add(n.id);
    const children = childrenOf(n.id).slice().sort(sortByName);
    const lvl = depth >= 2 ? 2 : depth;
    const editable = editableIds.has(n.id);
    return (
      <li key={n.id}>
        <div
          className={`oc-card lvl${lvl} ${dropTarget === n.id ? "drop-on" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDropTarget(n.id); }}
          onDragLeave={() => setDropTarget((t) => (t === n.id ? null : t))}
          onDrop={(e) => { e.preventDefault(); handleDrop(n.id); }}
        >
          {editable && (
            <span className="oc-handle" draggable onDragStart={() => { dragId.current = n.id; }} title="Arraste para mudar o gestor">⠿</span>
          )}
          {editable && (
            <button className="oc-rm" title="Remover do organograma (volta para a lista)"
              onClick={() => removeFromTree(n)}>✕</button>
          )}
          <div className="oc-av">{initials(n.nmEmployee)}</div>
          <div className="oc-role">
            {editable ? (
              <input defaultValue={n.jobDescription ?? ""} placeholder="definir cargo"
                onBlur={(e) => { if ((e.target.value || "") !== (n.jobDescription || "")) onEditField(n.id, "jobDescription", e.target.value); }} />
            ) : (<span>{n.jobDescription || "—"}</span>)}
          </div>
          <div className="oc-person">{n.nmEmployee}</div>
          <div className="oc-area">
            {editable ? (
              <input defaultValue={n.department ?? ""} placeholder="+ área"
                onBlur={(e) => { if ((e.target.value || "") !== (n.department || "")) onEditField(n.id, "department", e.target.value); }} />
            ) : n.department ? (<span>{n.department}</span>) : null}
          </div>
        </div>
        {children.length > 0 && <ul>{children.map((c) => renderNode(c, depth + 1, seen))}</ul>}
      </li>
    );
  };

  const seen = new Set<number>();

  return (
    <div className="orgchart-wrap">
      <style>{ORG_CSS}</style>
      <div className="oc-layout">
        {/* Pool de não incluídos */}
        <aside
          className={`oc-pool ${dropTarget === "pool" ? "drop-on" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDropTarget("pool"); }}
          onDragLeave={() => setDropTarget((t) => (t === "pool" ? null : t))}
          onDrop={(e) => { e.preventDefault(); handleDrop(null); }}
        >
          <div className="oc-pool-head"><span>Não incluídos</span><span className="cnt">{pool.length}</span></div>
          <div className="oc-pool-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar usuário…" />
          </div>
          <div className="oc-pool-list">
            {pool.length ? pool.map((p) => {
              const editable = editableIds.has(p.id);
              return (
                <div key={p.id}
                  className={`oc-chip ${editable ? "" : "nd"} ${dropTarget === p.id ? "drop-on" : ""}`}
                  draggable={editable}
                  onDragStart={editable ? () => { dragId.current = p.id; } : undefined}
                  onDragOver={(e) => { e.preventDefault(); setDropTarget(p.id); }}
                  onDragLeave={() => setDropTarget((t) => (t === p.id ? null : t))}
                  onDrop={(e) => { e.preventDefault(); handleDrop(p.id); }}
                  title={editable ? "Arraste para cima de um gestor (ou de outra pessoa) para incluir" : "Sem permissão para incluir"}
                >
                  <div className="ca">{initials(p.nmEmployee)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="cn">{p.nmEmployee}</div>
                    <div className="ce">{p.email}</div>
                  </div>
                </div>
              );
            }) : <div className="oc-pool-empty">{q ? "Ninguém encontrado." : "🎉 Todos incluídos!"}</div>}
          </div>
        </aside>

        {/* Canvas da árvore */}
        <section className="oc-canvas">
          <div className="oc-toolbar">
            <div className="oc-hint">Arraste alguém da lista para <b>cima de outra pessoa</b> para criar o vínculo (quem recebe vira o gestor).</div>
            <div className="oc-zoom">
              <button onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} title="Reduzir">−</button>
              <button onClick={() => setZoom(1)} title="100%">{Math.round(zoom * 100)}%</button>
              <button onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} title="Ampliar">+</button>
            </div>
          </div>
          <div className="oc-scroll">
            <div className="oc-tree" style={{ zoom } as React.CSSProperties}>
              {roots.length ? (
                <ul className="oc">{roots.map((r) => renderNode(r, 0, seen))}</ul>
              ) : (
                <div className="oc-empty">Ninguém na árvore ainda. Arraste uma pessoa da lista <b>para cima de outra</b> para começar — quem recebe vira o topo.</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const ORG_CSS = `
.orgchart-wrap{display:flex;flex-direction:column;gap:12px}
.oc-layout{display:flex;gap:14px;align-items:flex-start}
.oc-canvas{flex:1;min-width:0}
.oc-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:8px}
.oc-hint{font-size:12.5px;color:#5c6879;max-width:60ch}
.oc-zoom{display:flex;gap:2px;background:#fff;border:1px solid #e1e5ec;border-radius:9px;padding:3px}
.oc-zoom button{border:0;background:transparent;font-weight:700;min-width:34px;height:30px;border-radius:6px;cursor:pointer;color:#1b2230}
.oc-zoom button:hover{background:#f4f6fa}
.oc-pool{flex:0 0 250px;max-height:72vh;display:flex;flex-direction:column;border:1px solid #e1e5ec;border-radius:12px;background:#fff;overflow:hidden}
.oc-pool.drop-on{outline:2px solid #f26419;outline-offset:-2px}
.oc-pool-head{padding:10px 12px;border-bottom:1px solid #e1e5ec;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#5c6879;display:flex;justify-content:space-between;align-items:center}
.oc-pool-head .cnt{color:#b7791f;font-family:ui-monospace,"Cascadia Mono",Consolas,monospace}
.oc-pool-search{margin:8px;display:flex;align-items:center;gap:6px;background:#f4f6fa;border:1px solid #e1e5ec;border-radius:8px;padding:6px 9px;color:#8a94a0}
.oc-pool-search input{border:0;background:transparent;outline:none;width:100%;font-size:13px;color:#1b2230}
.oc-pool-list{flex:1;overflow-y:auto;padding:0 8px 10px}
.oc-chip{display:flex;align-items:center;gap:8px;padding:6px 8px;border:1px solid #e1e5ec;border-radius:9px;background:#fff;margin-bottom:6px;cursor:grab}
.oc-chip.nd{cursor:default;opacity:.6}
.oc-chip:active{cursor:grabbing}
.oc-chip.drop-on{outline:2px solid #f26419}
.oc-chip .ca{width:26px;height:26px;border-radius:7px;display:grid;place-items:center;font-size:10.5px;font-weight:700;color:#fff;background:#6d5fe0;flex:0 0 auto}
.oc-chip .cn{font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.oc-chip .ce{font-size:10.5px;color:#8a94a0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.oc-pool-empty{padding:20px;text-align:center;color:#8a94a0;font-size:12.5px}
.oc-scroll{overflow:auto;padding:8px 4px 40px}
.oc-tree{width:max-content;min-width:100%}
.oc,.oc ul{list-style:none;margin:0;padding:0}
.oc{display:flex;justify-content:center}
.oc ul{display:flex;justify-content:center;padding-top:22px;position:relative}
.oc li{display:flex;flex-direction:column;align-items:center;position:relative;padding:22px 8px 0}
.oc li::before,.oc li::after{content:'';position:absolute;top:0;right:50%;width:50%;height:22px;border-top:2px solid #c6cfdb}
.oc li::after{right:auto;left:50%;border-left:2px solid #c6cfdb}
.oc li:only-child::before,.oc li:only-child::after{display:none}
.oc li:only-child{padding-top:22px}
.oc li:first-child::before,.oc li:last-child::after{border:0}
.oc li:last-child::before{border-right:2px solid #c6cfdb;border-radius:0 7px 0 0}
.oc li:first-child::after{border-radius:7px 0 0 0}
.oc ul::before{content:'';position:absolute;top:0;left:50%;height:22px;border-left:2px solid #c6cfdb}
.oc>li{padding-top:0}
.oc>li::before,.oc>li::after{display:none}
.oc-card{position:relative;width:190px;border-radius:16px;padding:16px 12px 12px;display:flex;flex-direction:column;align-items:center;text-align:center;border:1px solid transparent;box-shadow:0 10px 20px -12px rgba(30,40,70,.28)}
.oc-card.drop-on{outline:2.5px solid #f26419;outline-offset:2px}
.oc-av{width:46px;height:46px;border-radius:50%;display:grid;place-items:center;font-weight:700;font-size:13px;margin-bottom:9px;background:#eef0f8;color:#6d5fe0}
.oc-role input,.oc-area input{width:100%;text-align:center;background:transparent;border:1px solid transparent;border-radius:6px;color:inherit;font:inherit;padding:1px 4px}
.oc-role input,.oc-role span{font-weight:700;font-size:13px}
.oc-area input{font-size:11px;opacity:.85;margin-top:2px}
.oc-role input:focus,.oc-area input:focus{outline:none;border-color:rgba(130,140,160,.5);background:rgba(255,255,255,.15)}
.oc-role input::placeholder,.oc-area input::placeholder{color:currentColor;opacity:.5}
.oc-person{font-size:12px;margin-top:3px;opacity:.9}
.oc-area span{font-size:11px;opacity:.75}
.oc-handle{position:absolute;top:6px;left:8px;cursor:grab;opacity:.55;font-size:14px;line-height:1;letter-spacing:-2px}
.oc-handle:active{cursor:grabbing}
.oc-card .oc-rm{position:absolute;top:5px;right:7px;border:0;background:transparent;color:inherit;cursor:pointer;opacity:.45;font-size:13px;line-height:1;padding:2px 4px;border-radius:6px}
.oc-card .oc-rm:hover{opacity:1;background:rgba(0,0,0,.12)}
.oc-card.lvl0{background:linear-gradient(160deg,#2f6dff,#1b46c9);color:#fff}
.oc-card.lvl0 .oc-av{background:rgba(255,255,255,.2);color:#fff}
.oc-card.lvl1{background:linear-gradient(160deg,#6e5ee8,#5a49d6);color:#fff}
.oc-card.lvl1 .oc-av{background:rgba(255,255,255,.2);color:#fff}
.oc-card.lvl2{background:#fff;color:#1b2230;border-color:#e1e5ec}
.oc-card.lvl0 .oc-person,.oc-card.lvl1 .oc-person{color:#fff}
.oc-card.lvl2 .oc-person{color:#6d5fe0;opacity:1}
.oc-empty{color:#5c6879;text-align:center;padding:30px;font-size:13px;max-width:460px;margin:0 auto}
.dark .oc-zoom,.dark .oc-pool{background:#171c24;border-color:#2a313b}
.dark .oc-zoom button{color:#e6ebf0}
.dark .oc-zoom button:hover{background:#1e242e}
.dark .oc-hint,.dark .oc-empty{color:#93a0ad}
.dark .oc-pool-head{color:#93a0ad;border-color:#2a313b}
.dark .oc-pool-search{background:#0b1437;border-color:#2a313b}
.dark .oc-pool-search input{color:#e6ebf0}
.dark .oc-chip{background:#1e242e;border-color:#2a313b}
.dark .oc-chip .cn{color:#e6ebf0}
.dark .oc li::before,.dark .oc li::after,.dark .oc li:last-child::before,.dark .oc ul::before{border-color:#38414e}
.dark .oc-card.lvl2{background:#1e242e;color:#e6ebf0;border-color:#2a313b}
.dark .oc-av{background:#242b36;color:#9b8ff0}
.dark .oc-card.lvl2 .oc-person{color:#9b8ff0}
`;
